import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { FormikContextType } from 'formik';
import cn from 'classnames';
import AddIcon from '@mui/icons-material/Add';
import {
	Box,
	FormControl,
	FormHelperText,
	InputLabel,
	TextField,
	TextFieldProps,
	Theme,
} from '@mui/material';
import { deepmerge } from '@mui/utils';
import { FieldAdditionalProps } from 'shared/_ui/form/types';
import { getFieldGridSx } from 'shared/_ui/form/utils';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { AddTagButton, TagChip } from './styles';

function TagFieldStyled(props: TextFieldProps & FieldAdditionalProps) {
	const {
		className,
		labelProps,
		readOnly = false,
		onReadOnlyClick,
		onClick,
		onChange,
		defaultValue,
		value,
		...fieldProps
	} = props;
	const { label, helperText } = fieldProps;
	const { slotProps, name, sx = {}, fullWidth } = fieldProps;

	const [tags, setTags] = useState<string[]>(Array.isArray(defaultValue) ? defaultValue : []);
	useEffect(() => {
		// Support controlled state
		if (value && Array.isArray(value)) {
			setTags(value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Array.isArray(value) ? value?.join(',') : '']);

	const [inputValue, setInputValue] = useState('');

	const handleChange = useCallback(
		(newTags: string[]) => {
			setTimeout(() => {
				const event = {
					target: { name, value: newTags },
				} as unknown as ChangeEvent<HTMLInputElement>;
				onChange?.(event);
			}, 1);
		},
		[name, onChange]
	);

	const addTag = useCallback(
		(tag: string) => {
			const normalizedTag = tag.toLowerCase();
			setTags((old) => {
				const normalizedOld = old.map((t) => t.toLowerCase());
				if (normalizedTag && !normalizedOld.includes(normalizedTag)) {
					const newTags = [...old, tag];
					handleChange(newTags);
					return newTags;
				}
				return old;
			});
			setInputValue('');
		},
		[handleChange]
	);

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			addTag(inputValue.trim());
		} else if (e.key === 'Backspace' && inputValue === '') {
			setTags((old) => {
				const newTags = old.slice(0, -1);
				handleChange(newTags);
				return newTags;
			});
		}
	};

	const handleDelete = (tagToDelete: string) => {
		const normalizedTag = tagToDelete.toLowerCase();
		setTags((old) => {
			const newTags = old.filter((tag) => tag.toLowerCase() !== normalizedTag);
			handleChange(newTags);
			return newTags;
		});
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};

	const sxProps = deepmerge(sx ?? {}, {
		'&.field': {
			...(fullWidth ? { width: '100%' } : {}),
			...getFieldGridSx({ label, labelProps, helperText }),
		},
		'.MuiFormLabel-root': {
			gridArea: 'label',
			...(labelProps?.width ? { width: labelProps.width } : {}),
			maxWidth: '100%',
		},
		'.MuiInputBase-input': {
			gridArea: 'value',
			flex: 1,
			minWidth: '5em',
			display:
				readOnly || (slotProps?.input as { readOnly?: boolean } | undefined)?.readOnly
					? 'none'
					: undefined,
		},
		'.MuiFormHelperText-root': {
			gridArea: 'help',
		},
		'.MuiInputBase-root': {
			flexWrap: 'wrap',
			gap: (theme) => theme.spacing(2),
		},
		'.MuiChip-deleteIcon': {
			display: (slotProps?.input as { readOnly?: boolean } | undefined)?.readOnly
				? 'none'
				: undefined,
		},
	} as SystemStyleObject<Theme>);

	return (
		<TextField
			id={name}
			{...fieldProps}
			onClick={readOnly ? onReadOnlyClick ?? onClick : onClick}
			className={cn('field', 'editable', className)}
			placeholder="Type and press Enter or Comma"
			value={inputValue}
			onChange={handleInputChange}
			onKeyDown={handleKeyDown}
			slotProps={{
				...slotProps,
				input: {
					...slotProps?.input,
					readOnly,
					startAdornment: (
						<>
							{tags.map((tag) => (
								<TagChip
									key={tag}
									label={tag}
									onDelete={readOnly ? undefined : () => handleDelete(tag)}
								/>
							))}
							{readOnly && onReadOnlyClick && (
								<AddTagButton
									onClick={(e) => {
										e.stopPropagation();
										onReadOnlyClick();
									}}
								>
									<AddIcon />
									Add tag
								</AddTagButton>
							)}
						</>
					),
				},
			}}
			sx={sxProps}
		/>
	);
}

type CommonTextFieldProps = Omit<
	TextFieldProps,
	'value' | 'onChange' | 'onBlur' | 'error' | 'helperText'
> &
	FieldAdditionalProps;

type FormTagFieldProps = CommonTextFieldProps & {
	formik: FormikContextType<any>;
	readOnly?: boolean;
	onReadOnlyClick?: () => void;
	name: string;
};

type FieldTagFieldProps = Pick<
	TextFieldProps,
	'className' | 'name' | 'label' | 'sx' | 'fullWidth' | 'error' | 'helperText'
> &
	FieldAdditionalProps & {
		value?: string[];
	};

export function FieldTagField(props: FieldTagFieldProps) {
	const { className, name, sx, label, labelProps, fullWidth, value, helperText, ...fieldProps } =
		props;
	const { error } = fieldProps;

	const sxLabel = {
		...(sx?.['.MuiFormLabel-root' as keyof typeof sx] ?? {}),
		...(labelProps?.width ? { width: labelProps.width } : {}),
	};

	// Use grid to move label to the correct position under the value
	const sxProps = deepmerge(sx ?? {}, {
		width: fullWidth ? '100%' : 'fit-content',
		...getFieldGridSx({ label, labelProps, helperText }),
		'.MuiFormLabel-root': {
			gridArea: 'label',
			maxWidth: '100%',
		},
		'.MuiTypography-root': {
			gridArea: 'value',
		},
		'.MuiFormHelperText-root': {
			gridArea: 'help',
		},
	});

	return (
		<FormControl
			className={cn('field', className)}
			sx={sxProps}
			fullWidth={fullWidth}
			{...fieldProps}
		>
			{!!label && (
				<InputLabel htmlFor={name} sx={sxLabel} id={`${name}-label`}>
					{label}
				</InputLabel>
			)}
			<Box
				sx={{
					display: 'inline-flex',
					gap: 2,
					flexWrap: 'wrap',
					width: fullWidth ? '100%' : undefined,
					flex: fullWidth ? 1 : undefined,
				}}
			>
				{value?.map((tag) => (
					<TagChip key={tag} label={tag} />
				))}
			</Box>
			{helperText && (
				<FormHelperText id={`${name}-helper-text`} error={error} sx={{ flexBasis: '100%' }}>
					{helperText}
				</FormHelperText>
			)}
		</FormControl>
	);
}

// export default function SwitchField(props: FieldProps<SwitchProps>) {
export function FormTagField({ formik, ...props }: FormTagFieldProps) {
	const { name } = props;

	const helperText = formik.errors[name] as TextFieldProps['helperText']; // formik.touched[name] &&
	const error = Boolean(formik.errors[name]); // formik.touched[name] &&

	return (
		<TagFieldStyled
			id={name}
			{...props}
			value={formik.values[name]}
			onChange={formik.handleChange}
			onBlur={formik.handleBlur}
			error={error}
			helperText={helperText}
		/>
	);
}
